import type { FormField } from '@funkit/connect-core'
import { useEffect, useRef, useState } from 'react'
import { Animated, Easing } from 'react-native'

import type { KycSectionPage } from './formLayout'
import { pickedDateField } from './formLayout'
import { KYC_FORM_METRICS } from './kycFormTheme'
import {
  DATE_PICKER_IS_INLINE,
  type NativeDatePicker,
  useNativeDatePicker,
} from './nativeDatePicker'

export interface DatePickerDock {
  /** The page's date a picker answers, absent when none can. */
  field?: FormField
  /** Set whenever a picker could answer this page's date, open or not. */
  Picker?: NativeDatePicker
  /** The native peer is still resolving for this page's date. */
  isLoading: boolean
  isOpen: boolean
  /** Pressing the field raises the picker, and presses it away again. */
  toggle: () => void
  close: () => void
  /** Room the picker takes while up; 0 on a page that has none. */
  height: number
  /** 0 closed, 1 open, on the keyboard's own curve. */
  slide: Animated.Value
}

/**
 * The date picker a form page stands over.
 *
 * Held here rather than in the screen so the form does not carry the rules
 * about which platform draws what: the screen only needs to know how much
 * room to give up and what to draw in it.
 */
export function useDatePickerDock(
  /** Absent while the form is on its faceplate, or has no page to draw. */
  page: KycSectionPage | undefined,
  step: number,
): DatePickerDock {
  const picker = useNativeDatePicker()
  const [isOpen, setIsOpen] = useState(false)
  const slide = useRef(new Animated.Value(0)).current

  // Closed on arrival, and closed again on the way to the next page. The
  // form's other pages raise no keyboard until a field is touched, and a
  // picker standing open under an untouched field reads as the same misfire.
  useEffect(() => {
    setIsOpen(false)
  }, [step])

  // The keyboard's own timing, so the picker arriving in its slot reads as
  // the same gesture rather than a panel appearing.
  useEffect(() => {
    Animated.timing(slide, {
      toValue: isOpen ? 1 : 0,
      duration: KEYBOARD_SLIDE_MS,
      easing: KEYBOARD_EASING,
      // `bottom` and layout are not transform properties.
      useNativeDriver: false,
    }).start()
  }, [isOpen, slide])

  const pageDateField = page === undefined ? undefined : pickedDateField(page)
  const field =
    picker === null || picker === undefined ? undefined : pageDateField

  return {
    ...(field === undefined ? {} : { field }),
    ...(field === undefined || picker === null || picker === undefined
      ? {}
      : { Picker: picker }),
    isLoading: pageDateField !== undefined && picker === undefined,
    isOpen: field !== undefined && isOpen,
    toggle: () => setIsOpen((open) => !open),
    close: () => setIsOpen(false),
    // An inline picker is a surface the screen gives room to. Android's is a
    // dialog over everything, which needs none.
    height:
      field !== undefined && DATE_PICKER_IS_INLINE
        ? KYC_FORM_METRICS.datePickerHeight
        : 0,
    slide,
  }
}

/** iOS animates its keyboard over this long, on this curve. */
const KEYBOARD_SLIDE_MS = 250
const KEYBOARD_EASING = Easing.bezier(0.17, 0.59, 0.4, 0.77)
